<template>
  <div class="container mt-4">


    <h1>📅 DateField Component</h1>
    <p>
      A Vue 3 date picker component based on <code>@vuepic/vue-datepicker</code>
      with <code>v-model</code> support, custom formatting, month picker mode, required field, and error display.
    </p>

    <label class="form-label">Documentation
      <a href="https://vue3datepicker.com/props/modes/" target="_blank">Vue Datepicker</a>
    </label>

    <hr />

    <h2>✨ Features</h2>
    <ul>
      <li>🔄 Two-way binding via <code>v-model</code></li>
      <li>🏷 Label with optional required indicator</li>
      <li>🗓 Standard date mode or month picker mode (<code>isMonthMode</code>)</li>
      <li>⚡ Supports custom date format (<code>format</code>)</li>
      <li>⚠️ Built-in error display</li>
    </ul>

    <hr />

    <h2>📦 Props</h2>
    <ul>
      <li><b>modelValue</b> (String | Date | Object, default: null) — bound date value</li>
      <li><b>label</b> (String) — label text</li>
      <li><b>isMonthMode</b> (Boolean, default: false) — enable month picker mode</li>
      <li><b>required</b> (Boolean, default: false) — mark field as required</li>
      <li><b>format</b> (String | Function, default: null) — custom date format</li>
      <li><b>error</b> (String) — error message text</li>
    </ul>

    <hr />

    <h2>📡 Usage Examples</h2>

    <!-- Example 1: Basic DatePicker -->
    <h3>Basic DatePicker</h3>
    <date-field
        v-model="dateValue"
        label="Select Date"
    />
    <p>Current value: {{ dateValue }}</p>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      &lt;date-field
        v-model="dateValue"
        label="Select Date"
      /&gt;

      data() {
        return {
          dateValue: null
        }
      }
    </code></pre>

    <!-- Example 2: Month Picker Mode -->
    <h3>Month Picker Mode</h3>
    <date-field
        v-model="monthValue"
        label="Select Month"
        :isMonthMode="true"
    />
    <p>Current value: {{ monthValue }}</p>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      &lt;date-field
        v-model="monthValue"
        label="Select Month"
        :isMonthMode="true"
      /&gt;

      data() {
        return {
          monthValue: null
        }
      }
    </code></pre>

    <!-- Example 3: With Validation -->
    <h3>With Required Validation</h3>
    <date-field
        v-model="dateError"
        label="Event Date"
        required
        :error="!dateError ? 'This field is required' : ''"
    />
    <p>Current value: {{ dateError }}</p>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      &lt;date-field
        v-model="dateError"
        label="Event Date"
        required
        :error="!dateError ? 'This field is required' : ''"
      /&gt;

      data() {
        return {
          dateError: null
        }
      }
    </code></pre>

    <!-- Example 4: Custom Format -->
    <h3>Custom Date Format</h3>
    <date-field
        v-model="formattedDate"
        label="Formatted Date"
        :format="'dd.MM.yyyy'"
    />
    <p>Current value: {{ formattedDate }}</p>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      &lt;date-field
        v-model="formattedDate"
        label="Formatted Date"
        :format="'dd.MM.yyyy'"
      /&gt;

      data() {
        return {
          formattedDate: null
        }
      }
    </code></pre>

  </div>
</template>

<script>
import DateField from "@/components/common/date-picker-field/DatePickerField.vue";

export default {
  name: "DateFieldDocs",
  components: {
    DateField
  },
  data() {
    return {
      dateValue: null,
      monthValue: null,
      dateError: null,
      formattedDate: null
    };
  }
};
</script>